var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { css, styled } from '../theme-provider';
import { createStylesForBreakpoints, } from '../helpers/breakpoint';
import { warnWithTrace } from '../helpers/logger';
import { getCommonProps } from '../helpers/common-props';
import { TextAppearance as TokenTextAppearance, } from '@volkswagen-onehub/tokens/';
export { TokenTextAppearance };
export var TextTag;
(function (TextTag) {
    TextTag["span"] = "span";
    TextTag["div"] = "div";
    TextTag["p"] = "p";
    TextTag["h1"] = "h1";
    TextTag["h2"] = "h2";
    TextTag["h3"] = "h3";
    TextTag["h4"] = "h4";
    TextTag["h5"] = "h5";
    TextTag["sub"] = "sub";
    TextTag["sup"] = "sup";
    TextTag["strong"] = "strong";
    TextTag["del"] = "del";
    TextTag["legend"] = "legend";
    TextTag["li"] = "li";
    TextTag["dt"] = "dt";
    TextTag["dd"] = "dd";
})(TextTag || (TextTag = {}));
export var TextAlignment;
(function (TextAlignment) {
    TextAlignment["center"] = "center";
    TextAlignment["left"] = "left";
    TextAlignment["right"] = "right";
    TextAlignment["start"] = "start";
    TextAlignment["end"] = "end";
})(TextAlignment || (TextAlignment = {}));
export var TokenTextColor;
(function (TokenTextColor) {
    TokenTextColor["primary"] = "primary";
    TokenTextColor["secondary"] = "secondary";
    TokenTextColor["inherit"] = "inherit";
    TokenTextColor["tertiary"] = "tertiary";
    TokenTextColor["inverted"] = "inverted";
    TokenTextColor["active"] = "active";
})(TokenTextColor || (TokenTextColor = {}));
export var WhiteSpace;
(function (WhiteSpace) {
    WhiteSpace["normal"] = "normal";
    WhiteSpace["nowrap"] = "nowrap";
    WhiteSpace["pre"] = "pre";
    WhiteSpace["preLine"] = "pre-line";
    WhiteSpace["preWrap"] = "pre-wrap";
    WhiteSpace["initial"] = "initial";
    WhiteSpace["inherit"] = "inherit";
})(WhiteSpace || (WhiteSpace = {}));
const createTextColorStyles = (props) => {
    const { theme, textColor } = props;
    const colorPath = theme.colors.content;
    const colorsObj = {
        [TokenTextColor.inherit]: 'inherit',
        [TokenTextColor.primary]: colorPath.primary,
        [TokenTextColor.secondary]: colorPath.secondary,
        [TokenTextColor.tertiary]: colorPath.tertiary,
        [TokenTextColor.inverted]: colorPath.inverted,
        [TokenTextColor.active]: colorPath.active,
    };
    const color = textColor
        ? colorsObj[textColor]
        : colorsObj[TokenTextColor.primary];
    return `
		color: ${color};
	`;
};
function createTokenFontStyles(props) {
    var _a;
    const { bold, staticSize, sub, sup, appearance = 'copy200', theme } = props;
    const [, fontKey, fontSizeKey] = appearance.match(/([a-z]{1,25})(\d{1,10})/);
    const fontProperties = theme.fonts[fontKey][parseInt(fontSizeKey, 10)];
    if (!fontProperties) {
        warnWithTrace(`The textAppearance ${appearance} could not be found`);
        return '';
    }
    const fontWeight = bold ? 'bold' : ((_a = fontProperties.fontWeight) !== null && _a !== void 0 ? _a : 'normal');
    const { fontFamily, fontSize, letterSpacing, lineHeight, reducedFontSize } = staticSize ? Object.assign(Object.assign({}, fontProperties), fontProperties.static) : fontProperties;
    // var(--font-family-text) and var(--font-family-head) are set in the integrator-root component
    return `
		margin: unset;
		font-family: ${(fontFamily === null || fontFamily === void 0 ? void 0 : fontFamily.startsWith('vw-head')) ? 'var(--font-family-head)' : 'var(--font-family-text)'};
		font-weight: ${fontWeight};
		font-size: ${sub || sup ? reducedFontSize : fontSize};
		line-height: ${sub || sup ? '0' : lineHeight};
		letter-spacing: ${letterSpacing};
	`;
}
function createTextAlignStylesForBreakpoints(props) {
    if (!props.textAlign) {
        return '';
    }
    return createStylesForBreakpoints(props.textAlign, (textAlign) => `text-align: ${textAlign};`);
}
const StyledTextComponent = styled.span.withConfig({ displayName: "StyledTextComponent", componentId: "sc-hqqa9q" }) `position:${(props) => (props.sub || props.sup) && 'static'};vertical-align:${(props) => {
    if (props.sup) {
        return 'super';
    }
    if (props.sub) {
        return 'sub';
    }
    return '';
}};${({ wordBreak }) => wordBreak &&
    css `
			word-break: normal;
		`} ${({ wordBreak, hyphens }) => wordBreak &&
    typeof hyphens === 'undefined' &&
    css `
			overflow-wrap: anywhere;
		`} ${({ hyphens }) => hyphens &&
    css `
			hyphens: ${hyphens};
		`} &:lang(ko){word-break:keep-all;}${createTextColorStyles} ${createTokenFontStyles} ${createTextAlignStylesForBreakpoints} ${({ strikethrough }) => {
    if (typeof strikethrough === 'undefined') {
        return;
    }
    return `text-decoration: ${strikethrough ? 'line-through' : 'initial'};`;
}} white-space:${(props) => props.whiteSpace};`;
StyledTextComponent.displayName = 'StyledTextComponent';
export const TextContext = React.createContext({});
/**
 * Usually React.memo has no big effect on components that take children,
 * because the children are a new object reference on each render - The text
 * component however is often used with just a string as children, which makes
 * this optimization work well in those cases.
 */
export const Text = React.memo((props) => {
    const context = React.useContext(TextContext);
    const { appearance, bold = context.bold, children, className, id, color = context.textColor, staticSize = context.staticSize, strikethrough, sub, sup, tag = (sub && TextTag.sub) ||
        (sup && TextTag.sup) ||
        (context.textAppearance && TextTag.span) ||
        TextTag.div, textAlign, wordBreak, whiteSpace, hyphens } = props, rest = __rest(props, ["appearance", "bold", "children", "className", "id", "color", "staticSize", "strikethrough", "sub", "sup", "tag", "textAlign", "wordBreak", "whiteSpace", "hyphens"]);
    const commonProps = getCommonProps(props);
    const textAppearance = appearance || context.textAppearance || TokenTextAppearance.copy200;
    const innerContextValue = React.useMemo(() => ({
        textAppearance,
        bold,
        textColor: color,
        staticSize,
    }), [textAppearance, bold, color, staticSize]);
    return (React.createElement(StyledTextComponent, Object.assign({ as: tag, appearance: textAppearance, bold: bold, textColor: color, staticSize: staticSize, strikethrough: strikethrough, sub: sub, sup: sup, textAlign: textAlign, wordBreak: wordBreak, whiteSpace: whiteSpace, hyphens: hyphens, className: className, id: id }, commonProps, rest),
        React.createElement(TextContext.Provider, { value: innerContextValue }, children)));
});
Text.displayName = 'Text';
